import React, { useMemo } from "react";
import { Badge, Table } from "react-bootstrap";
import moment from "moment";
import { usePaymentHistory } from "../../hooks/usePaymentHistory";
import LoadingLoader from "components/LoadingLoader";

const STATUS_VARIANT = {
  Paid: "success",
  Unpaid: "warning",
  "Waived/Comp": "info",
  Standard: "secondary",
  Frozen1: "danger",
  Frozen2: "danger",
  "Deactivated/Unsubscribed": "dark",
};

/**
 * Renders the payment-status change audit trail (frozenStatusHistory) so an
 * admin can see who changed a company's status, when, and why — without the
 * Stripe dashboard. Data comes from the /payment-history endpoint.
 */
const StatusHistoryView = ({ companyId, enabled = true }) => {
  const { data, isLoading } = usePaymentHistory(companyId, enabled);

  const history = useMemo(() => {
    const entries = data?.frozenStatusHistory || [];
    // Most recent first.
    return [...entries].sort(
      (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    );
  }, [data]);

  if (isLoading) return <LoadingLoader />;

  if (!history.length) {
    return <p className="text-muted mb-0">No status changes recorded yet.</p>;
  }

  return (
    <div>
      <h5 className="mb-3">Payment Status History</h5>
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>#</th>
            <th>Status</th>
            <th>Changed At</th>
            <th>Changed By</th>
            <th>Reason</th>
            <th>Waived Until</th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, idx) => (
            <tr key={`${entry.changedAt}-${idx}`}>
              <td>{idx + 1}</td>
              <td>
                <Badge bg={STATUS_VARIANT[entry.status] || "secondary"}>
                  {entry.status}
                </Badge>
              </td>
              <td>
                {entry.changedAt
                  ? moment(entry.changedAt).format("MMM DD, YYYY h:mm A")
                  : "—"}
              </td>
              <td>{entry.changedBy ? String(entry.changedBy) : "System"}</td>
              <td>{entry.reason || "—"}</td>
              <td>
                {entry.waivedEndDate
                  ? moment(entry.waivedEndDate).format("MMM DD, YYYY")
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default StatusHistoryView;
